/**
 * Prefill Create Demand from an existing debtor contact record.
 */

import { debtorDisplayName } from './debtorIdentity.js';
import { normalizeDebtorCountry } from './debtorCountry.js';
import { debtorAccountNumber } from './debtorRecords.js';

/**
 * Map a debtor list/detail record (or matter-like fields) into Intake form fields.
 */
export function intakeFormFromDebtor(debtor = {}) {
  if (!debtor || typeof debtor !== 'object') return null;

  const name = String(
    debtor.name ?? debtor.Debtor_Name ?? debtor.debtorName ?? ''
  ).trim();
  const company = String(
    debtor.company ?? debtor.Debtor_Company ?? debtor.debtorCompany ?? ''
  ).trim();
  const state = String(
    debtor.state ?? debtor.Debtor_State ?? debtor.debtorState ?? ''
  ).trim();
  const country = normalizeDebtorCountry(
    debtor.country ?? debtor.Debtor_Country ?? debtor.debtorCountry,
    {
      postalCode: debtor.postalCode ?? debtor.Debtor_Postal_Code ?? debtor.debtorPostalCode,
      state,
    }
  );

  if (!name && !company) return null;

  return {
    debtorName: name,
    debtorCompany: company,
    debtorCountry: country || 'US',
    debtorStreet: String(
      debtor.street ?? debtor.Debtor_Street ?? debtor.debtorStreet ?? ''
    ).trim(),
    debtorCity: String(debtor.city ?? debtor.Debtor_City ?? debtor.debtorCity ?? '').trim(),
    debtorState: state,
    debtorPostalCode: String(
      debtor.postalCode ?? debtor.Debtor_Postal_Code ?? debtor.debtorPostalCode ?? ''
    ).trim(),
    debtorEmail: String(
      debtor.email ?? debtor.Debtor_Email ?? debtor.debtorEmail ?? ''
    ).trim(),
    externalAccountNumber: debtorAccountNumber(debtor),
  };
}

/**
 * Merge a saved debtor into a Create Demand form.
 * Account # is filled only when that debtor already has one.
 */
export function buildIntakeFormFromDebtorPrefill(base = {}, prefill) {
  if (!prefill) return base;
  return {
    ...base,
    ...prefill,
    externalAccountNumber: debtorAccountNumber(prefill),
  };
}

export function intakePrefillLabel(debtor = {}) {
  const fromFields = debtorDisplayName({
    Debtor_Name: debtor.name ?? debtor.Debtor_Name,
    Debtor_Company: debtor.company ?? debtor.Debtor_Company,
  });
  if (fromFields) return fromFields;
  return 'Selected debtor';
}

/**
 * Build react-router location state for /client/demands/new.
 */
export function intakeLocationStateFromDebtor(debtor, extras = {}) {
  if (!debtor?.id && !debtor?.name && !debtor?.company && !debtor?.Debtor_Name) {
    return extras.from ? { from: extras.from } : {};
  }
  return {
    fromDebtor: true,
    debtorId: debtor.id || null,
    debtorLabel: intakePrefillLabel(debtor),
    debtorPrefill: intakeFormFromDebtor(debtor),
    from: extras.from || undefined,
  };
}
